// ** Third Party Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setLoader } from "./loaderSlice";
import { client } from '..';
import {
  CREATE_LEAD, CREATE_LEAD_FILTER, CREATE_LEAD_WORKHISTORY, GET_ALL_LEAD_FILTERS_BY_ID, GET_ALL_LEAD_WORKHISTORY, GET_ALL_LEAD_WORKHISTORY_BY_ID,
  GET_ALL_LEADS, GET_LEAD_BY_ID, LEADS_FILTERS, UPDATE_LAST_LEADS_CALL, UPDATE_LEAD, GET_PIPELINE_LEADS, BULK_UPDATE_LEADS,
  CREATE_EMAIL_SEQUENCE, UPDATE_EMAIL_SEQUENCE_STATUS, GET_EMAIL_SEQUENCES_BY_LEAD,
  GET_DUPLICATE_LEADS, MERGE_LEADS, SAVE_DRAFT, DISCARD_DRAFT,
} from "../Queries/Leads";
import { GET_ALL_EMAIL_TEMPLATES, ADD_EMAIL_TEMPLATE, UPDATE_EMAIL_TEMPLATE, DELETE_EMAIL_TEMPLATE } from "../Queries/emailTemplates";
import { GET_ALL_SIGNATURES, ADD_SIGNATURE, UPDATE_SIGNATURE, DELETE_SIGNATURE } from "../Queries/signatures";
import { message } from "antd";
import axios from "axios";
import { SERVER_URL } from "../databucket";
import { setLastContactedDates } from "./lastContactedDatesSlice";
import { EMAILQUERY, RE_VERIFY_EMAIL, UPDATE_CONTACT_VERIFICATION } from "../Queries/email";
import { Mutex } from "../blueplanitUtils/mutex";
import { closeWindow, closeAllWindows, clearWindowsOnLogout } from "./windowsSlice";
import {
  LEAD_LIST_CONTEXT, getLeadListContext, createLeadList, findLeadInLists,
} from "./leadListUtils";


const callUpdateMutex = new Mutex();

// ── Per-window lead lists ─────────────────────────────────────────────────────
// Every Leads / Pipeline window keeps its OWN list state (rows, filters, sort, page,
// scope, filter panel, KPI counts, filter options) under `leadLists[listKey]`, so two
// Leads windows can be filtered and searched independently side by side.
// listKey = "<context>:<windowId>" inside a webtop window, or just "<context>" outside
// one (legacy single-view behaviour). Lead data changes (socket updates, edits, pipeline
// toggles, new leads) are still applied to EVERY list so all windows stay fresh.
// Pure helpers live in ./leadListUtils (re-exported here for convenience).
export { LEAD_LIST_CONTEXT, getLeadListKey, selectLeadList, findLeadInLists } from "./leadListUtils";

// Request sequencing: only the latest response per list (and per request kind) is
// applied, so a slow earlier fetch can never overwrite a newer filter's results.
const latestLeadListRequest = {};
const startLeadListRequest = (kind, listKey) => {
  const id = `${kind}:${listKey}`;
  latestLeadListRequest[id] = (latestLeadListRequest[id] || 0) + 1;
  return latestLeadListRequest[id];
};
const isLatestLeadListRequest = (kind, listKey, seq) =>
  latestLeadListRequest[`${kind}:${listKey}`] === seq;

const ensureLeadList = (state, listKey) => {
  if (!state.leadLists[listKey]) {
    const context = getLeadListContext(listKey);
    state.leadLists[listKey] = createLeadList(context, state.leadScope[context]);
  }
  return state.leadLists[listKey];
};

const forEachLeadList = (state, fn) => {
  Object.values(state.leadLists).forEach(fn);
};

const removeWindowLeadLists = (state, windowId) => {
  Object.keys(state.leadLists).forEach((key) => {
    // Window-scoped keys only ("<context>:<id>"); legacy context-only keys stay.
    if (key.includes(":") && (windowId === undefined || key.endsWith(`:${windowId}`))) {
      delete state.leadLists[key];
    }
  });
};

const initialState = {
  // `sales` is for the Sales (Leads) view. `pipelineSales` is for the Pipeline view.
  // Kept as separate arrays per Rule 1 sync with Desktop (Desktop needs this because
  // its per-tab MemoryRouters mount both pages simultaneously and would otherwise
  // overwrite each other's data).
  sales: [],
  pipelineSales: [],
  pipelineLeads: [],
  sale: {},
  saleById: {},
  salesCounts: { total: 0, inPipeline: 0, overdue: 0, dueToday: 0 },
  leadsInfo: {},
  agencyInfo: {},
  contactsInfo: [],
  callsInfo: [],
  emailsInfo: [],
  linkedinInfo: [],
  schedulingCallsInfo: [],
  otcInfo: [],
  comments: {},
  contactComments: {},
  todayStats: {},
  monthlyStats: {},
  followUpStats: {},
  totalStats: {},
  savedFilters: [],
  refreshStatus: null,
  pageCount: 0,
  leadfilterObject: {
    sales: {},
    pipeline: {}
  },
  leadfilterObjectTemp: {},
  sortObject: {
    sales: {},
    pipeline: {}
  },
  filterSearch: '',
  page: {
    sales: 1,
    pipeline: 1
  },
  open: false,
  selectedFilterName: {
    sales: {},
    pipeline: {}
  },
  prevVisibility: false,
  salesWorkHistory: [],
  isRefetch: false,
  emailTemplates: [],
  signatures: [],
  leadFilters: {},
  workHistory: [],
  callActive: false,
  createSource: "sales",
  isPipeline: false,
  leadScope: (() => {
    try {
      const saved = JSON.parse(localStorage.getItem("leadScope"));
      if (saved && saved.sales && saved.pipeline) return saved;
    } catch {}
    return { sales: "MY", pipeline: "MY" };
  })(),
  leadLists: {},
};

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    getAllSales: (state, action) => {
      state.sales = action.payload
    },
    setPipelineSales: (state, action) => {
      state.pipelineSales = action.payload
    },
    updateSales: (state, action) => {
      state.sales = [action.payload, ...state.sales];
      // Mirror into pipelineSales so the Pipeline view gets real-time additions too
      if (action.payload?.isInPipeline === true) {
        state.pipelineSales = [action.payload, ...state.pipelineSales];
      }
      // Add to every open list. Skips lists that already have it (the lead:created socket
      // event and createLeadThunk both dispatch this, once per mounted window).
      const lead = action.payload;
      if (!lead?.id) return;
      forEachLeadList(state, (list) => {
        if (list.context === LEAD_LIST_CONTEXT.PIPELINE && lead.isInPipeline !== true) return;
        if (list.rows.some((l) => l.id === lead.id)) return;
        list.rows.unshift(lead);
        list.pageCount += 1;
      });
    },
    setSale: (state, action) => {
      state.sale = action.payload;
      if (action.payload?.id) {
        state.saleById[action.payload.id] = action.payload;
      }
    },
    setSavedFilters: (state, action) => {
      state.savedFilters = action.payload;
    },
    setRefreshStatus: (state, action) => {
      state.refreshStatus = action.payload;
    },
    setPageCount: (state, action) => {
      state.pageCount = action.payload;
    },
    setleadfilterObjectt: (state, action) => {
      const { context, filters } = action.payload;
      state.leadfilterObject[context] = filters;
    },

    setSortObjectt: (state, action) => {
      const { context, sort } = action.payload;
      state.sortObject[context] = sort;
    },
    setPage: (state, action) => {
      const { context, page } = action.payload;
      state.page[context] = page;
    },
    setOpen: (state, action) => {
      state.open = action.payload;
    },
    setSelectedFilterName: (state, action) => {
      const { context, filter } = action.payload;
      state.selectedFilterName[context] = filter;
    },

    setDivVisibility: (state, action) => {
      state.prevVisibility = action.payload;
    },
    setSalesWorkHistory: (state, action) => {
      state.salesWorkHistory = action.payload;
    },

    setIsRefetch: (state, action) => {
      state.isRefetch = action.payload;
    },
    setEmailTemplates: (state, action) => {
      state.emailTemplates = action.payload;
    },
    addEmailTemplateLocal: (state, action) => {
      state.emailTemplates.unshift(action.payload);
    },
    updateEmailTemplateLocal: (state, action) => {
      const idx = state.emailTemplates.findIndex(t => t.id === action.payload.id);
      if (idx !== -1) state.emailTemplates[idx] = action.payload;
    },
    deleteEmailTemplateLocal: (state, action) => {
      state.emailTemplates = state.emailTemplates.filter(t => t.id !== action.payload);
    },
    setSignatures: (state, action) => {
      state.signatures = action.payload;
    },
    addSignature: (state, action) => {
      state.signatures.unshift(action.payload);
    },
    updateSignatureInState: (state, action) => {
      const idx = state.signatures.findIndex(s => s.id === action.payload.id);
      if (idx !== -1) state.signatures[idx] = action.payload;
    },
    removeSignature: (state, action) => {
      state.signatures = state.signatures.filter(s => s.id !== action.payload);
    },
    setLeadFilters: (state, action) => {
      state.leadFilters = action.payload;
    },
    setSalesInfoleadsContacts: (state, action) => {
      if (state.sale) {
        // If updating leadsCalls with call recording data, merge per-call instead of replacing entire array.
        // This prevents Formik reinitialization from wiping user's in-progress edits.
        if (action.payload.leadsCalls && state.sale.leadsCalls) {
          const updatedCalls = action.payload.leadsCalls;
          // Handles both numeric-string timestamps (e.g. "1778556432511") and ISO date strings.
          // new Date(numericString) returns Invalid Date, so we try Number() first.
          const parseTime = (d) => {
            if (!d) return NaN;
            const n = Number(d);
            return !isNaN(n) ? n : new Date(d).getTime();
          };
          state.sale.leadsCalls = state.sale.leadsCalls.map((existingCall) => {
            // Find the matching call from the update by date (timestamp match)
            const updated = updatedCalls.find((uc) => {
              if (!uc.date || !existingCall.date) return false;
              const ucT = parseTime(uc.date);
              const exT = parseTime(existingCall.date);
              if (isNaN(ucT) || isNaN(exT)) return false;
              return Math.floor(ucT / 1000) === Math.floor(exT / 1000);
            });
            if (updated) {
              // Only merge recording-related fields from backend — preserve user's in-progress edits
              return {
                ...existingCall,
                recordingid: updated.recordingid || existingCall.recordingid,
                from: updated.from || existingCall.from,
                to: updated.to || existingCall.to,
                duration: updated.duration || existingCall.duration,
                // Preserve user edits: spokeWith, remarks, status, emailSent, callby
                // Only overwrite if existingCall has empty value AND updated has real data
                ...((!existingCall.status || existingCall.status === 'Unknown') && updated.status ? { status: updated.status } : {}),
              };
            }
            return existingCall;
          });
        } else {
          state.sale = {
            ...state.sale,
            ...action.payload,
          };
        }
        if (state.sale.id) {
          state.saleById[state.sale.id] = state.sale;
        }
      }
    },
    updateCallstatus: (state, action) => {
      state.callActive = action.payload
    },
    addLeadToPipeline: (state, action) => {
      const updatedLead = action.payload;

      // update sales list
      const salesIndex = state.sales.findIndex(
        lead => lead.id === updatedLead.id
      );
      if (salesIndex !== -1) {
        state.sales[salesIndex] = updatedLead;
      }
      forEachLeadList(state, (list) => {
        const i = list.rows.findIndex((lead) => lead.id === updatedLead.id);
        if (i !== -1) list.rows[i] = updatedLead;
      });

      // update pipeline list
      if (updatedLead.isInPipeline) {
        const exists = state.pipelineLeads.find(
          lead => lead.id === updatedLead.id
        );
        if (!exists) {
          state.pipelineLeads.push(updatedLead);
        }
      }
    },

    removeLeadFromPipeline: (state, action) => {
      const leadId = action.payload;
      state.pipelineLeads = state.pipelineLeads.filter(
        lead => lead.id !== leadId
      );
    },
    updateLocalLead: (state, action) => {
      const { leadId, changes } = action.payload;

      // Apply to every open window's list (independent of the legacy array below).
      forEachLeadList(state, (list) => {
        const i = list.rows.findIndex((lead) => lead.id === leadId);
        if (i !== -1) list.rows[i] = { ...list.rows[i], ...changes };
      });

      const salesIndex = state.sales.findIndex(
        (lead) => lead.id === leadId
      );

      if (salesIndex === -1) return;

      // Update sales list (single source of truth)
      state.sales[salesIndex] = {
        ...state.sales[salesIndex],
        ...changes,
      };

      // Sync pipelineLeads from sales
      if (changes.hasOwnProperty("isInPipeline")) {
        if (changes.isInPipeline) {
          const exists = state.pipelineLeads.some(
            (lead) => lead.id === leadId
          );
          if (!exists) {
            state.pipelineLeads.push(state.sales[salesIndex]);
          }
        } else {
          state.pipelineLeads = state.pipelineLeads.filter(
            (lead) => lead.id !== leadId
          );
        }
      }
    },
    setCreateSource: (state, action) => {
      state.createSource = action.payload; // "sales" | "pipeline"
    },
    setIsPipeline: (state, action) => {
      state.isPipeline = action.payload;
    },
    setLeadScope: (state, action) => {
      const { context, scope } = action.payload;
      state.leadScope[context] = scope;
      try { localStorage.setItem("leadScope", JSON.stringify(state.leadScope)); } catch {}
    },
    setSalesCounts: (state, action) => {
      state.salesCounts = { ...state.salesCounts, ...action.payload };
    },

    // ── Per-window lead list actions ──
    // UI-driven changes (filters, sort, page, panels…) for one list. Creates the list
    // if needed. payload: { key, changes }
    updateLeadList: (state, action) => {
      const { key, changes } = action.payload;
      Object.assign(ensureLeadList(state, key), changes);
    },
    // Async results (rows, counts, filter options). Ignored if the window was closed
    // while the request was in flight, so a late response can't resurrect its list.
    // payload: { key, changes }
    setLeadListData: (state, action) => {
      const { key, changes } = action.payload;
      const list = state.leadLists[key];
      if (list) Object.assign(list, changes);
    },
    // My/All scope for one list. Also stored as the default for newly opened windows.
    setLeadListScope: (state, action) => {
      const { key, scope } = action.payload;
      const list = ensureLeadList(state, key);
      list.leadScope = scope;
      state.leadScope[list.context] = scope;
      try { localStorage.setItem("leadScope", JSON.stringify(state.leadScope)); } catch {}
    },
    removeLeadList: (state, action) => {
      delete state.leadLists[action.payload];
    },

  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadByIdThunk.fulfilled, (state, action) => {
        state.sale = action.payload;
        // Also store by ID so multiple tabs don't overwrite each other
        if (action.payload?.id) {
          state.saleById[action.payload.id] = action.payload;
        }
        state.refreshStatus = null;
        // Optionally update lastContactedDate if needed
        if (action.payload?.lastContactedDate) {
          // If you want to store it in state, add a property for it
          state.lastContactedDate = parseInt(action.payload.lastContactedDate);
        }
      })
      .addCase(fetchLeadByIdThunk.rejected, (state, action) => {
        state.sale = {};
        state.refreshStatus = null;
      })
      .addCase(getAllLeadWorkHistory.fulfilled, (state, action) => {
        state.workHistory = action.payload;
      })
      .addCase(fetchPipelineLeads.fulfilled, (state, action) => {
        state.pipelineLeads = action.payload;
      })
      .addCase(fetchPipelineLeads.rejected, (state, action) => {
        state.pipelineLeads = [];
        message.error(action.payload || "Failed to fetch pipeline leads");
      })
      // A closed window's lead lists are dropped with it.
      .addCase(closeWindow, (state, action) => {
        removeWindowLeadLists(state, action.payload);
      })
      .addCase(closeAllWindows, (state) => {
        removeWindowLeadLists(state);
      })
      .addCase(clearWindowsOnLogout, (state) => {
        removeWindowLeadLists(state);
      });

  },
});


// Accepts isPipeline as an explicit param so callers can pin the value to their pathname
// instead of relying on global state.isPipeline (which gets corrupted in Desktop when
// multiple tabs mount SalesFilters concurrently). Frontend2.0 has a single shared router
// so it doesn't have the corruption issue today, but keeping signatures in sync with
// Desktop per Rule 1.
//
// listKey: fetch one window's list using that list's own filters/sort/page/scope.
// No listKey: refresh every open list (optionally only Sales or only Pipeline lists via
// isPipeline); with no open lists it falls back to the legacy shared fetch below.
export const fetchLeads = ({ silent = false, isPipeline: isPipelineParam, listKey } = {}) => async (dispatch, getState) => {
  if (listKey) return dispatch(fetchLeadList({ silent, listKey }));

  const openListKeys = Object.keys(getState().sales.leadLists).filter((key) =>
    isPipelineParam === undefined ||
    (getLeadListContext(key) === LEAD_LIST_CONTEXT.PIPELINE) === Boolean(isPipelineParam)
  );
  if (openListKeys.length > 0) {
    await Promise.all(openListKeys.map((key) => dispatch(fetchLeadList({ silent, listKey: key }))));
    return;
  }

  const { refreshStatus, isPipeline: isPipelineFromState } = getState().sales;
  const isPipeline = isPipelineParam !== undefined ? isPipelineParam : isPipelineFromState;
  const state = getState().sales;
  const { loginUserDetails } = getState().loginUserInfo;
  const userName = loginUserDetails?.firstname + " " + loginUserDetails?.lastname;

  const context = isPipeline ? "pipeline" : "sales";
  const page = state.page[context];
  const filters = state.leadfilterObject[context];
  const sort = state.sortObject[context];
  const leadScope = state.leadScope[context];
  if (refreshStatus) return;

  try {
    if (!silent) dispatch(setLoader(true));

    const { data } = await client.query({
      query: GET_ALL_LEADS,
      variables: {
        page,
        pageSize: 50,
        filters,
        sort,
        isPipeline: isPipeline || false,
        leadScope,
        assignedTo: leadScope === "MY" ? userName : null,
        loginUserId: loginUserDetails?.id,
      },
      fetchPolicy: "no-cache",
    });

    const leadsData = data?.getAllLeads;

    if (leadsData?.response) {
      dispatch(setPageCount(leadsData.count));
      // Route to the correct slot so Pipeline and Sales views don't overwrite each other.
      if (isPipeline) {
        dispatch(setPipelineSales(leadsData.response));
      } else {
        dispatch(getAllSales(leadsData.response));
      }
    }
    dispatch(setLoader(false));
  } catch (error) {
    console.error("Failed to fetch leads:", error);
  } finally {
    dispatch(setLoader(false));
  }
};

// Creates a list before its first request so the response is kept (setLeadListData
// ignores lists that are gone, i.e. windows closed mid-request).
const ensureLeadListExists = (dispatch, getState, listKey) => {
  if (!getState().sales.leadLists[listKey]) {
    dispatch(updateLeadList({ key: listKey, changes: {} }));
  }
};

// Fetches one window's lead list (see fetchLeads).
const fetchLeadList = ({ silent = false, listKey }) => async (dispatch, getState) => {
  ensureLeadListExists(dispatch, getState, listKey);
  const state = getState().sales;
  const list = state.leadLists[listKey];
  // refreshStatus = "keep the current rows" (set when returning from the create form).
  // A list that has never loaded must still fetch.
  if (state.refreshStatus && list.loaded) return;

  const { loginUserDetails } = getState().loginUserInfo;
  const userName = loginUserDetails?.firstname + " " + loginUserDetails?.lastname;
  const isPipeline = list.context === LEAD_LIST_CONTEXT.PIPELINE;
  const seq = startLeadListRequest("rows", listKey);

  try {
    if (!silent) dispatch(setLoader(true));

    const { data } = await client.query({
      query: GET_ALL_LEADS,
      variables: {
        page: list.page,
        pageSize: 50,
        filters: list.filters,
        sort: list.sort,
        isPipeline,
        leadScope: list.leadScope,
        assignedTo: list.leadScope === "MY" ? userName : null,
        loginUserId: loginUserDetails?.id,
      },
      fetchPolicy: "no-cache",
    });

    if (!isLatestLeadListRequest("rows", listKey, seq)) return;
    const leadsData = data?.getAllLeads;
    // The resolver reports bad filter input as a non-200 status with an empty list.
    if (leadsData?.status && leadsData.status !== 200 && leadsData.message) {
      message.error({ content: leadsData.message, key: "lead-list-fetch-error" });
    }
    if (leadsData?.response) {
      dispatch(setLeadListData({
        key: listKey,
        changes: { rows: leadsData.response, pageCount: leadsData.count, loaded: true },
      }));
      // Keep the legacy shared arrays in step for any reader not yet list-aware.
      dispatch(setPageCount(leadsData.count));
      dispatch(isPipeline ? setPipelineSales(leadsData.response) : getAllSales(leadsData.response));
    }
  } catch (error) {
    console.error("Failed to fetch leads:", error);
    // Keyed so several windows failing together show one toast, not one each.
    message.error({ content: "Failed to load leads. Please try again.", key: "lead-list-fetch-error" });
  } finally {
    dispatch(setLoader(false));
  }
};

// 🚀 Thunk to fetch all email templates for the logged-in employee
export const fetchAllEmailTemplates = (employeeId) => async (dispatch) => {
  try {
    const { data } = await client.query({
      query: GET_ALL_EMAIL_TEMPLATES,
      variables: { employeeId },
      fetchPolicy: 'network-only',
    });
    const result = data?.getAllEmailTemplates;
    if (result?.status) {
      dispatch(setEmailTemplates(result.response || []));
    } else {
      message.warning(result?.message || 'Could not fetch templates');
    }
  } catch (error) {
    console.error('Error fetching email templates:', error);
    message.error('Something went wrong while fetching templates');
  }
};

// 🚀 Thunk to create a new email template
export const saveEmailTemplate = (payload) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: ADD_EMAIL_TEMPLATE,
      variables: payload,
    });
    const result = data?.addEmailTemplate;
    if (result?.status && result?.response) {
      dispatch(addEmailTemplateLocal(result.response));
      message.success(result.message || 'Template created successfully');
      return { success: true };
    } else {
      message.warning(result?.message || 'Could not create template');
      return { success: false };
    }
  } catch (error) {
    console.error('Error saving email template:', error);
    message.error('Something went wrong while saving the template');
    return { success: false };
  }
};

// 🚀 Thunk to update an existing email template
export const editEmailTemplate = (payload) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: UPDATE_EMAIL_TEMPLATE,
      variables: payload,
    });
    const result = data?.updateEmailTemplate;
    if (result?.status && result?.response) {
      dispatch(updateEmailTemplateLocal(result.response));
      message.success(result.message || 'Template updated successfully');
      return { success: true };
    } else {
      message.warning(result?.message || 'Could not update template');
      return { success: false };
    }
  } catch (error) {
    console.error('Error updating email template:', error);
    message.error('Something went wrong while updating the template');
    return { success: false };
  }
};

// 🚀 Thunk to delete an email template
export const removeEmailTemplate = (id, employeeId) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: DELETE_EMAIL_TEMPLATE,
      variables: { id, employeeId },
    });
    const result = data?.deleteEmailTemplate;
    if (result?.status) {
      dispatch(deleteEmailTemplateLocal(id));
      message.success(result.message || 'Template deleted');
      return { success: true };
    } else {
      message.warning(result?.message || 'Could not delete template');
      return { success: false };
    }
  } catch (error) {
    console.error('Error deleting email template:', error);
    message.error('Something went wrong while deleting the template');
    return { success: false };
  }
};

// 🚀 Thunk to fetch all signatures for an employee
export const fetchAllSignatures = (employeeId) => async (dispatch) => {
  try {
    const { data } = await client.query({
      query: GET_ALL_SIGNATURES,
      variables: { employeeId },
      fetchPolicy: 'network-only',
    });
    const result = data?.getAllSignatures;
    if (result?.status) {
      dispatch(setSignatures(result.response || []));
    } else {
      message.warning(result?.message || 'Could not load signatures');
    }
  } catch (error) {
    console.error('Error fetching signatures:', error);
    message.error('Something went wrong while loading signatures');
  }
};

// 🚀 Thunk to create a new signature
export const saveSignature = (payload) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: ADD_SIGNATURE,
      variables: payload, // { name, content, employeeId }
    });
    const result = data?.addSignature;
    if (result?.status && result?.response) {
      dispatch(addSignature(result.response));
      message.success(result.message || 'Signature created successfully');
    } else {
      message.warning(result?.message || 'Could not create signature');
    }
  } catch (error) {
    console.error('Error creating signature:', error);
    message.error('Something went wrong while creating signature');
  }
};

// 🚀 Thunk to update an existing signature
export const updateSignatureThunk = (payload) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: UPDATE_SIGNATURE,
      variables: payload, // { id, name, content }
    });
    const result = data?.updateSignature;
    if (result?.status && result?.response) {
      dispatch(updateSignatureInState(result.response));
      message.success(result.message || 'Signature updated successfully');
    } else {
      message.warning(result?.message || 'Could not update signature');
    }
  } catch (error) {
    console.error('Error updating signature:', error);
    message.error('Something went wrong while updating signature');
  }
};

// 🚀 Thunk to delete a signature
export const deleteSignatureThunk = (id) => async (dispatch) => {
  try {
    const { data } = await client.mutate({
      mutation: DELETE_SIGNATURE,
      variables: { id },
    });
    const result = data?.deleteSignature;
    if (result?.status) {
      dispatch(removeSignature(id));
      message.success(result.message || 'Signature deleted');
    } else {
      message.warning(result?.message || 'Could not delete signature');
    }
  } catch (error) {
    console.error('Error deleting signature:', error);
    message.error('Something went wrong while deleting signature');
  }
};

export const fetchLeadFiltersThunk = createAsyncThunk(
  'leads/fetchLeadFilters',
  async ({ silent = false, listKey } = {}, { rejectWithValue, dispatch, getState }) => {
    const { refreshStatus } = getState().sales;
    const { loginUserDetails } = getState().loginUserInfo;
    const userName = loginUserDetails?.firstname + " " + loginUserDetails?.lastname;
    const state = getState().sales;

    // Per-window list: filter options reflect THAT window's filters and scope.
    if (listKey) {
      ensureLeadListExists(dispatch, getState, listKey);
      const list = getState().sales.leadLists[listKey];
      if (refreshStatus && list.loaded) return;
      const listScope = list.leadScope;
      const seq = startLeadListRequest("filters", listKey);
      try {
        if (!silent) dispatch(setLoader(true));
        const response = await client.query({
          query: LEADS_FILTERS,
          variables: {
            filters: list.filters,
            isPipeline: getLeadListContext(listKey) === LEAD_LIST_CONTEXT.PIPELINE,
            leadScope: listScope,
            assignedTo: listScope === "MY" ? userName : null
          },
          fetchPolicy: 'network-only',
        });
        const leadfilterdata = response?.data?.getLeadFilters;
        if (isLatestLeadListRequest("filters", listKey, seq)) {
          dispatch(setLeadListData({ key: listKey, changes: { leadFilters: leadfilterdata || {} } }));
        }
        return leadfilterdata;
      } catch (error) {
        return rejectWithValue(error.message || 'Error fetching lead filters');
      } finally {
        dispatch(setLoader(false));
      }
    }

    const { isPipeline } = state;
    const context = isPipeline ? "pipeline" : "sales";
    const filters = state.leadfilterObject[context];
    const leadScope = state.leadScope[context];
    if (refreshStatus) return;
    try {
      if (!silent) dispatch(setLoader(true));
      const response = await client.query({
        query: LEADS_FILTERS,
        variables: {
          filters: filters,
          isPipeline: isPipeline || false,
          leadScope: leadScope || "MY",
          assignedTo: leadScope === "MY" ? userName : null
        },
        fetchPolicy: 'network-only',
      });

      const leadfilterdata = response?.data?.getLeadFilters;
      dispatch(setLeadFilters(leadfilterdata));
      return leadfilterdata;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching lead filters');
    }
    finally {
      dispatch(setLoader(false));
    }
  }
);


export const createLeadThunk = createAsyncThunk(
  "sales/createLead",
  async ({ valuesObject, navigate, listKey }, { dispatch, getState, rejectWithValue }) => {
    try {

      const { data } = await client.mutate({
        mutation: CREATE_LEAD,
        variables: valuesObject,
      });

      const result = data?.createLead;
      if (!result) throw new Error("Lead creation failed");

      const { pageCount } = getState().sales;

      dispatch(setIsRefetch(true));
      dispatch(setRefreshStatus(false));
      dispatch(setDivVisibility(false));
      // Only the window the lead was created from resets its filters; other windows
      // keep theirs (they get the new lead through updateSales below).
      if (listKey) {
        dispatch(updateLeadList({ key: listKey, changes: { pendingReset: true, filterPanelOpen: false } }));
      }
      dispatch(setSale(result));
      dispatch(updateSales(result));
      dispatch(setPageCount(pageCount + 1));
      dispatch(setLastContactedDates([]));

      dispatch(
        updateLocalLead({
          leadId: result.id,
          changes: { isInPipeline: result.isInPipeline },
        })
      );

      message.success({
        content: `Lead created successfully! with lead Id ${result?.leadID}`,
        duration: 5,
        className: "custom-toast-center",
      });


      return result;
    } catch (error) {
      console.error("Create lead failed:", error);
      message.error("Something went wrong while creating lead");
      return rejectWithValue(error.message || "Unknown error occurred");
    } finally {
      dispatch(setLoader(false));
    }
  }
);
export const fetchPipelineLeads = createAsyncThunk(
  "sales/fetchPipelineLeads",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_PIPELINE_LEADS,
        fetchPolicy: "network-only", // IMPORTANT
      });

      return data.getPipelineLeads;
    } catch (error) {
      console.error("Failed to fetch pipeline leads", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateLeadPipelineStatus = createAsyncThunk(
  "sales/updateLeadPipelineStatus",
  async ({ leadId, isInPipeline }, { dispatch }) => {
    const { data } = await client.mutate({
      mutation: UPDATE_LEAD,
      variables: {
        id: leadId,
        isInPipeline,
      },
    });

    const updatedLead = data?.updateLead;
    if (!updatedLead) {
      throw new Error("Pipeline update failed");
    }

    // 🔥 TRUST BACKEND RESPONSE
    dispatch(
      updateLocalLead({
        leadId: updatedLead.id,
        changes: updatedLead,
      })
    );


    return updatedLead;
  }
);


export const updatePipelineStageThunk = createAsyncThunk(
  "sales/updatePipelineStage",
  async ({ leadId, pipelineStage }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_LEAD,
        variables: {
          id: leadId,
          pipelineStage,
          lastUpdatedDate: Date.now().toString(),
        },
      });
      const updatedLead = data?.updateLead;
      if (!updatedLead) throw new Error("Stage update failed");
      dispatch(updateLocalLead({ leadId: updatedLead.id, changes: { pipelineStage: updatedLead.pipelineStage } }));
      return updatedLead;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const validateEmailThunk = createAsyncThunk(
  "leads/validateEmail",
  async (variables, { rejectWithValue }) => {
    try {
      const response = await client.mutate({
        mutation: EMAILQUERY,
        variables,
      });

      return response
    } catch (error) {
      return rejectWithValue(error.message || "Email validation failed");
    }
  }
);

export const reVerifyEmailThunk = createAsyncThunk(
  "leads/reVerifyEmail",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await client.mutate({
        mutation: RE_VERIFY_EMAIL,
        variables: { email },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Re-verification failed");
    }
  }
);

export const updateContactVerificationThunk = createAsyncThunk(
  "leads/updateContactVerification",
  async ({ leadId, email, status, verifiedAt, verifyResult, verifyCode, verifyReason }, { rejectWithValue }) => {
    try {
      await client.mutate({
        mutation: UPDATE_CONTACT_VERIFICATION,
        variables: { leadId, email, status, verifiedAt, verifyResult, verifyCode, verifyReason },
      });
      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLeadByIdThunk = createAsyncThunk(
  'sales/fetchLeadById',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_LEAD_BY_ID,
        variables: { id },
        fetchPolicy: 'network-only',
      });

      const SalesInfo = data?.getLeadById?.[0];

      if (!SalesInfo) throw new Error('Lead not found');
      return SalesInfo;

    } catch (error) {
      console.error("Error in fetchLeadByIdThunk:", error);
      return rejectWithValue(error.message || 'Failed to fetch lead');
    }
  }
);

export const createLeadWorkHistoryThunk = createAsyncThunk(
  'sales/createLeadWorkHistory',
  async (historyObject, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_LEAD_WORKHISTORY,
        variables: historyObject,
      });
      return data?.createLeadWorkHistory;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const getAllLeadWorkHistoryByIdThunk = createAsyncThunk(
  'sales/getAllLeadWorkHistoryById',
  async (workHistoryObj, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await client.mutate({
        mutation: GET_ALL_LEAD_WORKHISTORY_BY_ID,
        variables: workHistoryObj,
      });
      const result = data?.getAllLeadWorkHistoryByID || [];
      dispatch(setSalesWorkHistory(result));
      return result;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching work history');
    }
  }
);
export const getAllLeadWorkHistory = createAsyncThunk(
  'timer/getAllWorkHistoryByID',
  async (emailId, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setLoader(true))
      const { data } = await client.mutate({
        mutation: GET_ALL_LEAD_WORKHISTORY,
        variables: { emailId },
      });

      const taskData = data.getAllWorkHistoryByID || [];

      if (!taskData) {
        console.warn('No work history found for email:', emailId);
        return rejectWithValue('No work history found.');
      }

      return data.getAllWorkHistoryByID;

    } catch (error) {
      console.error('Error fetching work history:', error);
      return rejectWithValue('Error fetching work history');
    }
    finally {
      dispatch(setLoader(false))
    }
  }
);
export const updateLeadThunk = createAsyncThunk(
  'sales/updateLead',
  async ({ valuesObject, loginUser }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const leadId = valuesObject?.id;
    const existingLead = findLeadInLists(state.sales, leadId);

    // Lock validation: prevent reassignment of locked leads (but allow the owner to update their own lead)
    const now = Date.now();
    const unlockTime = existingLead?.assignUnlockAt
      ? Number(existingLead.assignUnlockAt)
      : null;
    const isActuallyLocked =
      existingLead?.isAssignLocked && (!unlockTime || now < unlockTime);
    const currentUserFullName = `${loginUser?.firstname} ${loginUser?.lastname}`;
    const isOwner = existingLead?.assignedTo === currentUserFullName;
    const isAdmin = loginUser?.role === "Admin" || loginUser?.role === "Super Admin";

    if (
      isActuallyLocked &&
      !isOwner &&
      !isAdmin &&
      valuesObject?.assignedTo &&
      valuesObject?.assignedTo !== existingLead.assignedTo
    ) {
      message.warning("This lead is locked. Only the assigned user or an Admin can reassign.");
      return rejectWithValue("Lead is locked");
    }

    try {
      const { data } = await client.mutate({
        mutation: UPDATE_LEAD,
        variables: valuesObject,
      });


      const updatedLead = data?.updateLead;
      if (!updatedLead) {
        throw new Error('UpdateLead mutation returned no data.');
      }

      // Activity Timeline is written server-side inside updateLead now (so mobile,
      // bulk paths, and future callers also get logged automatically). The mutation
      // returns the new entry on `updatedLead.newHistoryEntry` when fields actually
      // changed — append it optimistically so the UI updates without a refetch.
      const existingWorkHistory = state.sales.salesWorkHistory;
      if (updatedLead.newHistoryEntry) {
        dispatch(setSalesWorkHistory([...existingWorkHistory, updatedLead.newHistoryEntry]));
      }

      dispatch(setRefreshStatus(false));
      dispatch(setSale(updatedLead));
      dispatch(
        updateLocalLead({
          leadId: updatedLead.id,
          changes: updatedLead,
        })
      );

      message.success({
        content: 'Lead updated successfully!',
        duration: 5,
        className: 'custom-toast-center',
      });

      return updatedLead;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const fetchAllLeadFiltersByIDThunk = createAsyncThunk(
  'leads/fetchAllLeadFiltersByID',
  async (userID, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.query({
        query: GET_ALL_LEAD_FILTERS_BY_ID,
        variables: { userID },
      });

      const data = response?.data?.getAllLeadFilterByID;
      if (data) {
        dispatch(setSavedFilters(data));
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error fetching lead filters by ID');
    }
  }
);

export const createLeadFilterThunk = createAsyncThunk(
  'leads/createLeadFilter',
  async ({ filterObject }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_LEAD_FILTER,
        variables: filterObject,
      });

      const newFilter = data?.createLeadFilters;
      if (newFilter) {
        const { savedFilters } = getState().sales;
        dispatch(setSavedFilters([...savedFilters, newFilter]));
      }

      return newFilter;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create filter');
    }
  }
);

export const trackCallUpdateThunk = createAsyncThunk(
  'calls/trackCallUpdate',
  async ({ sessionId, leadID, lastcalltime }, { dispatch, getState, rejectWithValue }) => {
    if (!sessionId || !leadID || !lastcalltime) {
      return rejectWithValue('Missing required parameters');
    }
    dispatch(updateCallstatus(true));
    // Acquire mutex before proceeding
    await callUpdateMutex.lock();

    try {
      // Poll RingCentral call-logs with retry instead of fixed 30s delay.
      // RingCentral takes 5-45 seconds to make recording available after call ends.
      // We poll every 5 seconds, up to 60 seconds max (12 attempts).
      const MAX_ATTEMPTS = 12;
      const POLL_INTERVAL = 5000; // 5 seconds between each attempt
      const INITIAL_DELAY = 5000; // Wait 5 seconds before first attempt (RC needs minimum processing time)

      // Initial wait — RingCentral always needs a few seconds to process
      await new Promise((r) => setTimeout(r, INITIAL_DELAY));

      let matchedLog = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await axios.get(`${SERVER_URL}/api/call-logs`);
          matchedLog = response?.data?.find(
            (log) => log?.sessionId === sessionId
          );

          if (matchedLog) {
            console.log(`✅ Call log found on attempt ${attempt} for session:`, sessionId);
            break;
          }

          console.log(`⏳ Attempt ${attempt}/${MAX_ATTEMPTS}: Call log not ready yet for session:`, sessionId);
        } catch (fetchErr) {
          console.warn(`⚠️ Attempt ${attempt}/${MAX_ATTEMPTS}: Failed to fetch call logs:`, fetchErr.message);
        }

        // Wait before next attempt (unless this was the last one)
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        }
      }

      if (!matchedLog) {
        console.warn('⚠️ No matching log found after all attempts for session:', sessionId);
        dispatch(updateCallstatus(false));
        return 'Call log not found after retries.';
      }

      const callUpdate = {
        recordingid: matchedLog?.recording?.contentUri || '',
        from: matchedLog?.from?.phoneNumber || '',
        to: matchedLog?.to?.phoneNumber || '',
        duration: matchedLog?.duration || 0,
        status:
          matchedLog?.result ||
          matchedLog?.telephonyStatus ||
          'Unknown',
        time: '',
        date: '',
        callby: '',
        spokeWith: '',
        emailSent: null,
        remarks: '',
      };

      const mutationResponse = await client.mutate({
        mutation: UPDATE_LAST_LEADS_CALL,
        variables: {
          lastcalltime,
          leadID: leadID.toString(),
          callUpdate,
        },
      });

      // Only update Redux if user is still viewing the SAME lead.
      // If they navigated away, the DB is already updated — Redux will get fresh data
      // when they reopen this lead. Updating Redux for a different lead would corrupt it.
      const currentSale = getState()?.sales?.sale;
      const currentLeadId = currentSale?.leadID || currentSale?.id;
      if (currentLeadId && currentLeadId.toString() === leadID.toString()) {
        dispatch(setSalesInfoleadsContacts({ leadsCalls: mutationResponse.data.updateLastLeadsCall.leadsCalls }));
      }
      dispatch(updateCallstatus(false));
      return 'Call update successful.';
    } catch (err) {
      console.error('❌ Error during call update:', err);
      dispatch(updateCallstatus(false));
      return rejectWithValue(err.message || 'Unknown error occurred');
    } finally {
      // Always release the mutex lock
      callUpdateMutex.unlock();
      console.log('🔓 Lock released for sessionId:', sessionId);
    }
  }
);


export const {
  getAllSales,
  setPipelineSales,
  updateSales,
  setSale,
  setSavedFilters,
  setRefreshStatus,
  setPageCount,
  setleadfilterObjectt,
  setSortObjectt,
  setPage,
  setOpen,
  setSelectedFilterName,
  setDivVisibility,
  setSalesWorkHistory,
  setIsRefetch,
  setEmailTemplates,
  addEmailTemplateLocal,
  updateEmailTemplateLocal,
  deleteEmailTemplateLocal,
  setSignatures,
  addSignature,
  updateSignatureInState,
  removeSignature,
  setLeadFilters,
  setSalesInfoleadsContacts,
  updateCallstatus,
  addLeadToPipeline,
  removeLeadFromPipeline,
  updateLocalLead,
  setCreateSource,
  setIsPipeline,
  setLeadScope,
  setSalesCounts,
  updateLeadList,
  setLeadListData,
  setLeadListScope,
  removeLeadList,
} = salesSlice.actions;

// listKey: counts for one window's list (its filters + scope). Without it, the legacy
// shared counts are used.
export const fetchSalesCountsThunk = ({ listKey } = {}) => async (dispatch, getState) => {
  const state = getState().sales;
  const { loginUserDetails } = getState().loginUserInfo;
  const userName = loginUserDetails?.firstname + " " + loginUserDetails?.lastname;
  const { isPipeline } = state;
  const context = isPipeline ? "pipeline" : "sales";
  if (listKey) ensureLeadListExists(dispatch, getState, listKey);
  const list = listKey ? getState().sales.leadLists[listKey] : null;
  const currentFilters = list ? list.filters : state.leadfilterObject[context];
  const leadScope = list ? list.leadScope : state.leadScope[context];
  const assignedTo = leadScope === "MY" ? userName : null;
  const seq = listKey ? startLeadListRequest("counts", listKey) : null;

  const today = new Date();
  const todayStart = new Date(today); todayStart.setHours(0, 0, 0, 0);
  const todayEnd   = new Date(today); todayEnd.setHours(23, 59, 59, 999);

  try {
    const [totalRes, pipelineRes, overdueRes, dueTodayRes] = await Promise.all([
      client.query({
        query: GET_ALL_LEADS,
        variables: { page: 1, pageSize: 1, filters: {}, sort: {}, isPipeline: false, leadScope, assignedTo },
        fetchPolicy: "no-cache",
      }),
      client.query({
        query: GET_ALL_LEADS,
        variables: { page: 1, pageSize: 1, filters: {}, sort: {}, isPipeline: true, leadScope, assignedTo },
        fetchPolicy: "no-cache",
      }),
      client.query({
        query: GET_ALL_LEADS,
        variables: {
          page: 1, pageSize: 1, sort: {}, isPipeline: false, leadScope, assignedTo,
          filters: { ...currentFilters, followup: "true", lastFollowupTo: todayStart.toISOString() },
        },
        fetchPolicy: "no-cache",
      }),
      client.query({
        query: GET_ALL_LEADS,
        variables: {
          page: 1, pageSize: 1, sort: {}, isPipeline: false, leadScope, assignedTo,
          filters: { ...currentFilters, followup: "true", lastFollowupFrom: todayStart.toISOString(), lastFollowupTo: todayEnd.toISOString() },
        },
        fetchPolicy: "no-cache",
      }),
    ]);

    const counts = {
      total:      totalRes.data?.getAllLeads?.count ?? 0,
      inPipeline: pipelineRes.data?.getAllLeads?.count ?? 0,
      overdue:    overdueRes.data?.getAllLeads?.count ?? 0,
      dueToday:   dueTodayRes.data?.getAllLeads?.count ?? 0,
    };
    if (listKey) {
      if (isLatestLeadListRequest("counts", listKey, seq)) {
        dispatch(setLeadListData({ key: listKey, changes: { counts } }));
      }
      return;
    }
    dispatch(setSalesCounts(counts));
  } catch (err) {
    console.error("fetchSalesCountsThunk failed:", err);
  }
};

export const bulkUpdateLeadsThunk = createAsyncThunk(
  'sales/bulkUpdateLeads',
  async (variables, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: BULK_UPDATE_LEADS,
        variables,
      });
      return data.bulkUpdateLeads;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createEmailSequenceThunk = createAsyncThunk(
  'sales/createEmailSequence',
  async (variables, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_EMAIL_SEQUENCE,
        variables,
      });
      return data.createEmailSequence;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateEmailSequenceStatusThunk = createAsyncThunk(
  'sales/updateEmailSequenceStatus',
  async ({ sequenceId, status }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_EMAIL_SEQUENCE_STATUS,
        variables: { sequenceId, status },
      });
      return data.updateEmailSequenceStatus;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getEmailSequencesByLeadThunk = createAsyncThunk(
  'sales/getEmailSequencesByLead',
  async ({ leadId }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_EMAIL_SEQUENCES_BY_LEAD,
        variables: { leadId },
        fetchPolicy: 'network-only',
      });
      return data.getEmailSequencesByLead;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getDuplicateLeadsThunk = createAsyncThunk(
  'sales/getDuplicateLeads',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_DUPLICATE_LEADS,
        fetchPolicy: 'network-only',
      });
      return data.getDuplicateLeads;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const mergeLeadsThunk = createAsyncThunk(
  'sales/mergeLeads',
  async ({ primaryLeadId, duplicateLeadId }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: MERGE_LEADS,
        variables: { primaryLeadId, duplicateLeadId },
      });
      return data.mergeLeads;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ─── saveDraftThunk ───────────────────────────────────────────────────────────
// Saves (or updates) an email draft on the lead without sending.
// Returns the updated lead so the caller can refresh Formik values.leadsEmails.
export const saveDraftThunk = createAsyncThunk(
  'sales/saveDraft',
  async ({ leadId, draftId, emailFrom, emailedto, emailCC, emailBCC, subject, body, draftAttachments }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: SAVE_DRAFT,
        variables: { leadId, draftId, emailFrom, emailedto, emailCC, emailBCC, subject, body, draftAttachments },
      });
      return data.saveDraft;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ─── discardDraftThunk ────────────────────────────────────────────────────────
// Permanently removes a draft entry from the lead's leadsEmails array.
export const discardDraftThunk = createAsyncThunk(
  'sales/discardDraft',
  async ({ leadId, draftId }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: DISCARD_DRAFT,
        variables: { leadId, draftId },
      });
      return data.discardDraft;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export default salesSlice.reducer;
